<script setup>
import { computed, ref, watch } from 'vue';
import { PROVIDERS } from '../helpers';
import { useAdsI18n } from '../useAdsI18n';

const props = defineProps({
  referral: {
    type: Object,
    required: true,
  },
  thumbnailUrl: { type: String, default: '' },
});

const { t } = useAdsI18n();
const mediaLoadError = ref(false);

const hasVideo = computed(() => props.referral.media_type === 'video');
const mediaThumbnail = computed(() => {
  const candidate =
    props.thumbnailUrl ||
    props.referral.thumbnail_url ||
    props.referral.image_url ||
    '';
  try {
    const url = new URL(candidate, window.location.origin);
    return url.origin === window.location.origin || url.protocol === 'https:'
      ? url.href
      : null;
  } catch {
    return null;
  }
});
const showMedia = computed(
  () => !!mediaThumbnail.value && !mediaLoadError.value
);
const sourceUrl = computed(() => {
  try {
    const url = new URL(props.referral.source_url);
    return ['https:', 'http:'].includes(url.protocol) ? url.href : '';
  } catch {
    return '';
  }
});
const headline = computed(
  () => props.referral.headline || props.referral.title || ''
);
const mediaType = computed(() =>
  ['image', 'video'].includes(props.referral.media_type)
    ? props.referral.media_type
    : 'text'
);
const providerIcon = computed(() => PROVIDERS.meta.icon);

watch([() => props.referral, mediaThumbnail], () => {
  mediaLoadError.value = false;
});
</script>

<template>
  <li class="px-4 pt-3 pb-1 list-none" data-ads-conversation-referral>
    <div class="flex justify-start">
      <div
        class="w-full max-w-md overflow-hidden rounded-xl rounded-bl-sm border border-dashed border-n-amber-7 bg-n-alpha-1"
      >
        <div
          class="flex items-center gap-2 border-b border-n-amber-7/40 bg-n-amber-3 px-3 py-2"
        >
          <span
            aria-hidden="true"
            class="size-4 flex-shrink-0"
            :class="providerIcon"
          />
          <span class="text-xs font-medium text-n-amber-12">
            {{ t('conversation.ad') }}
          </span>
        </div>

        <div v-if="showMedia" class="relative bg-n-slate-3">
          <img
            class="block max-h-72 w-full object-cover"
            :src="mediaThumbnail"
            :alt="headline || t('conversation.ad')"
            @error="mediaLoadError = true"
          />
          <span
            v-if="hasVideo"
            class="pointer-events-none absolute inset-0 flex items-center justify-center"
          >
            <span
              class="flex size-12 items-center justify-center rounded-full bg-black/50"
            >
              <span class="i-lucide-play size-6 text-white" />
            </span>
          </span>
        </div>
        <div
          v-else-if="mediaType !== 'text'"
          class="flex items-center gap-2 border-b border-n-weak bg-n-alpha-2 px-3 py-4 text-xs text-n-slate-10"
        >
          <span
            aria-hidden="true"
            class="size-4 shrink-0"
            :class="hasVideo ? 'i-lucide-video' : 'i-lucide-image'"
          />
          <span>{{ t('conversation.previewUnavailable') }}</span>
        </div>

        <div class="space-y-1.5 p-3">
          <h4
            v-if="headline"
            class="whitespace-pre-line break-words text-sm font-semibold leading-tight text-n-slate-12"
          >
            {{ headline }}
          </h4>
          <p
            v-if="referral.body"
            class="whitespace-pre-line break-words text-sm leading-snug text-n-slate-11"
          >
            {{ referral.body }}
          </p>
          <a
            v-if="sourceUrl"
            :href="sourceUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="mt-1 inline-flex items-center gap-1 text-xs font-medium text-n-amber-11 hover:text-n-amber-12 hover:underline"
          >
            <span class="i-lucide-external-link size-3" />
            {{ t('conversation.viewAd') }}
          </a>
        </div>

        <div
          v-if="referral.welcome_message?.text"
          class="mt-0 border-t border-n-amber-7/30 px-3 pb-3 pt-1 text-xs text-n-slate-10"
        >
          <span class="font-medium text-n-slate-11">
            {{ t('conversation.prefilledMessage') }}
          </span>
          <span class="italic">{{ referral.welcome_message.text }}</span>
        </div>
      </div>
    </div>

    <div
      class="mb-1 ml-2 mt-2 flex items-center gap-1.5 text-xs text-n-slate-10"
    >
      <span class="i-lucide-corner-down-right size-3" />
      <span>{{ t('conversation.repliedBelow') }}</span>
    </div>
  </li>
</template>
